import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Badge } from "@/components/ui/badge";
import {
  GraduationCap, Lock, ChevronDown, ChevronUp,
  CheckCircle, XCircle, RotateCcw, ArrowLeft,
} from "lucide-react";

interface ExamQuestion {
  id: string;
  question_type: "mcq" | "fill_in_blank" | "short_answer" | "true_false";
  prompt: string;
  options?: string[] | null;
  explanation?: string;
  points: number;
}

interface FeedbackItem {
  questionId: string;
  prompt: string;
  userAnswer: string;
  correctAnswer: string;
  isCorrect: boolean;
  explanation: string;
  pointsEarned: number;
  pointsPossible: number;
}

interface ExamViewProps {
  skillNodeId: string;
  courseId?: string;
  nodeTitle: string;
  onProgressUpdate?: () => void;
}

type ExamPhase = "configure" | "generating" | "taking" | "results";

const GREEN_DEEP = "#1C3829";
const LIME = "hsl(65,65%,75%)";

const DIFFICULTY_LABELS: Record<string, { label: string; desc: string; color: string }> = {
  easy:   { label: "Easy",   desc: "Recall & comprehension", color: "bg-green-500/15 text-green-700 dark:text-green-300" },
  medium: { label: "Medium", desc: "Understanding & application", color: "bg-amber-500/15 text-amber-700 dark:text-amber-300" },
  hard:   { label: "Hard",   desc: "Analysis & synthesis", color: "bg-red-500/15 text-red-700 dark:text-red-300" },
};

// ── Mastery badge ─────────────────────────────────────────────────────────────
function MasteryInfo({ mastery }: { mastery: { tier: string; score: number | null } | null }) {
  if (!mastery) return null;
  const tierMap: Record<string, { label: string; color: string }> = {
    none:     { label: "Not yet assessed", color: "text-muted-foreground" },
    low:      { label: "Beginner",         color: "text-blue-600 dark:text-blue-400" },
    medium:   { label: "Developing",       color: "text-amber-600 dark:text-amber-400" },
    high:     { label: "Proficient",       color: "text-green-600 dark:text-green-400" },
    mastered: { label: "Mastered",         color: "text-emerald-600 dark:text-emerald-400" },
  };
  const info = tierMap[mastery.tier] ?? tierMap.none;
  return (
    <p style={{ fontSize: 13, color: "hsl(var(--muted-foreground))" }}>
      Current mastery:{" "}
      <span style={{ fontWeight: 600 }} className={info.color}>
        {info.label}
        {mastery.score != null ? ` (${Math.round(mastery.score * 100)}%)` : ""}
      </span>
    </p>
  );
}

// ── Results accordion row ─────────────────────────────────────────────────────
function FeedbackRow({ item }: { item: FeedbackItem }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      style={{
        borderRadius: 10,
        border: `1px solid ${item.isCorrect ? "hsl(142 76% 36% / 0.3)" : "hsl(0 84% 60% / 0.3)"}`,
        overflow: "hidden",
      }}
    >
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width: "100%", textAlign: "left", padding: "12px 14px",
          display: "flex", alignItems: "center", gap: 10,
          background: item.isCorrect ? "hsl(142 76% 36% / 0.07)" : "hsl(0 84% 60% / 0.07)",
          border: "none", cursor: "pointer",
        }}
      >
        {item.isCorrect
          ? <CheckCircle style={{ width: 15, height: 15, color: "hsl(142, 76%, 36%)", flexShrink: 0 }} />
          : <XCircle style={{ width: 15, height: 15, color: "hsl(0, 84%, 60%)", flexShrink: 0 }} />
        }
        <span style={{ flex: 1, fontSize: 13, fontWeight: 500, lineHeight: 1.4 }}>{item.prompt}</span>
        <span style={{ fontSize: 11, color: "hsl(var(--muted-foreground))", flexShrink: 0 }}>
          {item.pointsEarned}/{item.pointsPossible} pts
        </span>
        {open
          ? <ChevronUp style={{ width: 14, height: 14, flexShrink: 0 }} />
          : <ChevronDown style={{ width: 14, height: 14, flexShrink: 0 }} />
        }
      </button>
      {open && (
        <div style={{ padding: "12px 14px", fontSize: 13, borderTop: "1px solid hsl(var(--border))", display: "flex", flexDirection: "column", gap: 6 }}>
          <p><span style={{ fontWeight: 600 }}>Your answer:</span> {item.userAnswer || <em>No answer</em>}</p>
          {!item.isCorrect && (
            <p><span style={{ fontWeight: 600 }}>Correct answer:</span> {item.correctAnswer}</p>
          )}
          {item.explanation && (
            <p style={{ color: "hsl(var(--muted-foreground))" }}>{item.explanation}</p>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function ExamView({ skillNodeId, courseId, nodeTitle, onProgressUpdate }: ExamViewProps) {
  const [phase, setPhase] = useState<ExamPhase>("configure");

  // Configure state
  const [mastery, setMastery] = useState<{ tier: string; score: number | null } | null>(null);
  const [suggestedDifficulty, setSuggestedDifficulty] = useState<"easy" | "medium" | "hard">("medium");
  const [selectedDifficulty, setSelectedDifficulty] = useState<"easy" | "medium" | "hard">("medium");
  const [customPrompt, setCustomPrompt] = useState("");
  const [isMandatory, setIsMandatory] = useState(false);
  const [previousAttempts, setPreviousAttempts] = useState<number>(0);

  // Session / taking state
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [questions, setQuestions] = useState<ExamQuestion[]>([]);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [passingScore, setPassingScore] = useState(70);
  const [submitting, setSubmitting] = useState(false);
  const [generateError, setGenerateError] = useState<string | null>(null);

  // Results state
  const [result, setResult] = useState<{
    score: number; passed: boolean; passingScore: number; feedback: FeedbackItem[];
  } | null>(null);

  // ── Load mastery + previous attempt count on mount ──────────────────────────
  useEffect(() => {
    (async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user?.id) return;
      const userId = session.user.id;

      const [masteryRes, attemptsRes, examRes] = await Promise.all([
        supabase
          .from("learner_node_mastery")
          .select("mastery_score, confidence_tier")
          .eq("user_id", userId)
          .eq("node_id", skillNodeId)
          .maybeSingle(),
        supabase
          .from("skill_node_exam_attempts")
          .select("score, passed")
          .eq("user_id", userId)
          .order("created_at", { ascending: false })
          .limit(20),
        supabase
          .from("skill_node_exams")
          .select("difficulty, is_mandatory, passing_score")
          .eq("skill_node_id", skillNodeId)
          .maybeSingle(),
      ]);

      // Previous attempts
      const attempts = attemptsRes.data || [];
      setPreviousAttempts(attempts.length);

      // Mandatory lock
      const mandatory = examRes.data?.is_mandatory ?? false;
      setIsMandatory(mandatory);
      if (examRes.data?.passing_score) setPassingScore(examRes.data.passing_score);

      // Mastery
      if (masteryRes.data) {
        const m = { tier: masteryRes.data.confidence_tier || "none", score: masteryRes.data.mastery_score ?? null };
        setMastery(m);
        const inferred = m.score != null
          ? (m.score < 0.35 ? "easy" : m.score < 0.70 ? "medium" : "hard")
          : "medium";
        setSuggestedDifficulty(inferred);
        if (!mandatory) {
          setSelectedDifficulty(inferred);
        } else {
          const d = (examRes.data?.difficulty ?? "medium") as "easy" | "medium" | "hard";
          setSelectedDifficulty(d);
        }
      } else if (mandatory && examRes.data?.difficulty) {
        const d = examRes.data.difficulty as "easy" | "medium" | "hard";
        setSelectedDifficulty(d);
        setSuggestedDifficulty(d);
      } else {
        // Fall back to attempt history
        const bestScore = attempts.length ? Math.max(...attempts.map((a: any) => a.score ?? 0)) : 0;
        const inferred: "easy" | "medium" | "hard" =
          bestScore >= 85 ? "hard" : bestScore >= 50 ? "medium" : "easy";
        setSuggestedDifficulty(inferred);
        if (!mandatory) setSelectedDifficulty(inferred);
        setMastery({ tier: attempts.length ? "low" : "none", score: null });
      }
    })();
  }, [skillNodeId]);

  // ── Generate exam ────────────────────────────────────────────────────────────
  const generateExam = useCallback(async () => {
    setGenerateError(null);
    setPhase("generating");
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("Not authenticated");

      const { data, error } = await supabase.functions.invoke("generate-learner-exam", {
        body: {
          nodeId: skillNodeId,
          courseId: courseId ?? null,
          difficulty: selectedDifficulty,
          customPrompt: customPrompt.trim() || null,
          _authToken: session.access_token,
        },
      });

      if (error) throw error;
      if (!data?.questions?.length) throw new Error("No questions returned");

      setSessionId(data.sessionId);
      setQuestions(data.questions);
      setPassingScore(data.passingScore ?? 70);
      setAnswers({});
      setPhase("taking");
    } catch (e: any) {
      setGenerateError(e?.message ?? "Generation failed. Please try again.");
      setPhase("configure");
    }
  }, [skillNodeId, courseId, selectedDifficulty, customPrompt]);

  // ── Submit exam ──────────────────────────────────────────────────────────────
  const submitExam = useCallback(async () => {
    if (!sessionId) return;
    const unanswered = questions.filter(q => !answers[q.id]?.trim());
    if (unanswered.length) {
      alert(`Please answer all ${questions.length} questions before submitting.`);
      return;
    }
    setSubmitting(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("Not authenticated");

      const { data, error } = await supabase.functions.invoke("submit-learner-exam", {
        body: {
          sessionId,
          answers: Object.entries(answers).map(([questionId, answerValue]) => ({ questionId, answerValue })),
          _authToken: session.access_token,
        },
      });

      if (error) throw error;

      setResult({
        score: data.score,
        passed: data.passed,
        passingScore: data.passingScore ?? passingScore,
        feedback: data.feedbackPerQuestion ?? [],
      });
      setPhase("results");
      if (data.passed) onProgressUpdate?.();
    } catch (e: any) {
      alert(e?.message ?? "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }, [sessionId, questions, answers, passingScore, onProgressUpdate]);

  const retake = () => {
    setResult(null);
    setAnswers({});
    setSessionId(null);
    setQuestions([]);
    setPhase("configure");
  };

  // ── Configure screen ─────────────────────────────────────────────────────────
  if (phase === "configure") {
    return (
      <div style={{
        maxWidth: 560, margin: "0 auto", padding: "28px 16px 80px",
        display: "flex", flexDirection: "column", gap: 24,
      }}>
        {/* Header */}
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12, textAlign: "center" }}>
          <div style={{
            width: 60, height: 60, borderRadius: 30,
            background: GREEN_DEEP, display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <GraduationCap style={{ width: 28, height: 28, color: LIME }} />
          </div>
          <div>
            <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 4 }}>Mock quiz: {nodeTitle}</h2>
            <p style={{ fontSize: 13, color: "hsl(var(--muted-foreground))" }}>
              AI-generated practice. To complete the lesson and unlock the next step, pass the instructor exam in the Read tab.
            </p>
            <p style={{ fontSize: 13, color: "hsl(var(--muted-foreground))", marginTop: 8 }}>
              {previousAttempts > 0
                ? `You've taken a mock quiz here ${previousAttempts} time${previousAttempts > 1 ? "s" : ""} before.`
                : "Your first mock quiz — difficulty adapts to your level."}
            </p>
          </div>
          <MasteryInfo mastery={mastery} />
        </div>

        {/* Mandatory notice */}
        {isMandatory && (
          <div style={{
            borderRadius: 10, padding: "12px 14px",
            background: "hsl(var(--muted))", border: "1px solid hsl(var(--border))",
            display: "flex", gap: 10, alignItems: "flex-start",
          }}>
            <Lock style={{ width: 15, height: 15, marginTop: 1, flexShrink: 0 }} />
            <p style={{ fontSize: 13, lineHeight: 1.5 }}>
              For this mock quiz, your instructor locked difficulty — practice only; it does not replace the lesson exam.
            </p>
          </div>
        )}

        {/* Difficulty selector */}
        <div>
          <p style={{ fontSize: 13, fontWeight: 600, marginBottom: 10 }}>
            Difficulty
            {!isMandatory && suggestedDifficulty && (
              <span style={{ fontWeight: 400, color: "hsl(var(--muted-foreground))", marginLeft: 8 }}>
                — suggested: {DIFFICULTY_LABELS[suggestedDifficulty]?.label}
              </span>
            )}
          </p>
          <div style={{ display: "flex", gap: 8 }}>
            {(["easy", "medium", "hard"] as const).map(d => {
              const info = DIFFICULTY_LABELS[d];
              const selected = selectedDifficulty === d;
              return (
                <button
                  key={d}
                  disabled={isMandatory}
                  onClick={() => setSelectedDifficulty(d)}
                  style={{
                    flex: 1, padding: "10px 8px", borderRadius: 10,
                    border: selected ? `2px solid ${GREEN_DEEP}` : "2px solid hsl(var(--border))",
                    background: selected ? `${GREEN_DEEP}10` : "hsl(var(--card))",
                    cursor: isMandatory ? "not-allowed" : "pointer",
                    opacity: isMandatory ? 0.6 : 1,
                    textAlign: "center",
                  }}
                >
                  <p style={{ fontSize: 13, fontWeight: 600, marginBottom: 2 }}>{info.label}</p>
                  <p style={{ fontSize: 11, color: "hsl(var(--muted-foreground))" }}>{info.desc}</p>
                </button>
              );
            })}
          </div>
        </div>

        {/* Custom prompt */}
        {!isMandatory && (
          <div>
            <Label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 8 }}>
              Customise this mock quiz <span style={{ fontWeight: 400, color: "hsl(var(--muted-foreground))" }}>(optional)</span>
            </Label>
            <Textarea
              value={customPrompt}
              onChange={e => setCustomPrompt(e.target.value)}
              placeholder='e.g. "Focus on practical applications", "Include more scenario questions", "Test edge cases"'
              rows={3}
              style={{ resize: "none", fontSize: 13 }}
            />
          </div>
        )}

        {/* Error */}
        {generateError && (
          <p style={{ fontSize: 13, color: "hsl(0, 84%, 60%)", textAlign: "center" }}>{generateError}</p>
        )}

        {/* CTA */}
        <Button
          onClick={generateExam}
          size="lg"
          style={{ background: GREEN_DEEP, color: LIME, width: "100%", gap: 8 }}
        >
          <GraduationCap style={{ width: 16, height: 16 }} />
          Generate mock quiz
        </Button>

        <p style={{ fontSize: 11, color: "hsl(var(--muted-foreground))", textAlign: "center" }}>
          Passing score: {passingScore}% · ~8 questions · AI practice (does not unlock the path)
        </p>
      </div>
    );
  }

  // ── Generating screen ────────────────────────────────────────────────────────
  if (phase === "generating") {
    return (
      <div style={{
        display: "flex", flexDirection: "column", alignItems: "center",
        justifyContent: "center", height: "calc(100vh - 100px)", gap: 20, padding: "0 16px",
      }}>
        <div style={{
          width: 64, height: 64, borderRadius: 32, background: GREEN_DEEP,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <GraduationCap style={{ width: 28, height: 28, color: LIME }} />
        </div>
        <div style={{ textAlign: "center" }}>
          <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 6 }}>Generating your mock quiz…</h2>
          <p style={{ fontSize: 13, color: "hsl(var(--muted-foreground))" }}>
            Building {DIFFICULTY_LABELS[selectedDifficulty]?.label.toLowerCase()} questions from all course materials.
          </p>
        </div>
        {/* Dots */}
        <div style={{ display: "flex", gap: 6 }}>
          {[0, 0.2, 0.4].map((delay, i) => (
            <span key={i} className="tutor-typing-dot" style={{ animationDelay: `${delay}s` }} />
          ))}
        </div>
      </div>
    );
  }

  // ── Taking screen ────────────────────────────────────────────────────────────
  if (phase === "taking") {
    const answeredCount = questions.filter(q => answers[q.id]?.trim()).length;
    return (
      <div style={{ maxWidth: 680, margin: "0 auto", padding: "20px 16px 100px" }}>
        {/* Header */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          marginBottom: 20, gap: 12,
        }}>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <Badge variant="outline" style={{ fontSize: 11 }}>{DIFFICULTY_LABELS[selectedDifficulty]?.label}</Badge>
            <span style={{ fontSize: 13, color: "hsl(var(--muted-foreground))" }}>
              {questions.length} questions · Pass: {passingScore}%
            </span>
          </div>
          <span style={{ fontSize: 13, color: "hsl(var(--muted-foreground))" }}>
            {answeredCount}/{questions.length} answered
          </span>
        </div>

        {/* Progress bar */}
        <div style={{ height: 4, borderRadius: 2, background: "hsl(var(--muted))", marginBottom: 24, overflow: "hidden" }}>
          <div style={{
            height: "100%", borderRadius: 2, background: GREEN_DEEP,
            width: `${questions.length > 0 ? (answeredCount / questions.length) * 100 : 0}%`,
            transition: "width 0.3s ease",
          }} />
        </div>

        {/* Questions */}
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          {questions.map((q, idx) => (
            <div
              key={q.id}
              style={{
                borderRadius: 14, border: "1px solid hsl(var(--border))",
                padding: "18px 20px", background: "hsl(var(--card))",
              }}
            >
              <p style={{ fontSize: 12, color: "hsl(var(--muted-foreground))", marginBottom: 8 }}>
                Question {idx + 1} of {questions.length}
              </p>
              <p style={{ fontSize: 15, fontWeight: 500, lineHeight: 1.55, marginBottom: 14 }}>
                {q.prompt}
              </p>

              {(q.question_type === "mcq" || q.question_type === "true_false") && q.options && (
                <RadioGroup
                  value={answers[q.id] || ""}
                  onValueChange={v => setAnswers(prev => ({ ...prev, [q.id]: v }))}
                >
                  {q.options.map(opt => (
                    <div key={opt} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                      <RadioGroupItem value={opt} id={`q-${q.id}-${opt}`} />
                      <Label htmlFor={`q-${q.id}-${opt}`} style={{ cursor: "pointer", fontSize: 14 }}>
                        {opt}
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              )}

              {q.question_type === "fill_in_blank" && (
                <Input
                  placeholder="Your answer"
                  value={answers[q.id] || ""}
                  onChange={e => setAnswers(prev => ({ ...prev, [q.id]: e.target.value }))}
                  style={{ maxWidth: 360 }}
                />
              )}

              {q.question_type === "short_answer" && (
                <Textarea
                  placeholder="Write your answer here…"
                  value={answers[q.id] || ""}
                  onChange={e => setAnswers(prev => ({ ...prev, [q.id]: e.target.value }))}
                  rows={3}
                  style={{ resize: "none" }}
                />
              )}
            </div>
          ))}
        </div>

        {/* Submit */}
        <div style={{ marginTop: 28, display: "flex", gap: 12, alignItems: "center" }}>
          <Button
            onClick={submitExam}
            disabled={submitting || answeredCount < questions.length}
            size="lg"
            style={{ background: GREEN_DEEP, color: LIME, flex: 1 }}
          >
            {submitting ? "Submitting…" : `Submit mock quiz (${answeredCount}/${questions.length} answered)`}
          </Button>
          <Button variant="outline" onClick={retake} disabled={submitting}>
            <ArrowLeft style={{ width: 14, height: 14 }} />
          </Button>
        </div>
      </div>
    );
  }

  // ── Results screen ───────────────────────────────────────────────────────────
  if (phase === "results" && result) {
    const pct = result.score;
    const passed = result.passed;
    const correctCount = result.feedback.filter(f => f.isCorrect).length;

    return (
      <div style={{ maxWidth: 640, margin: "0 auto", padding: "28px 16px 80px" }}>
        {/* Score card */}
        <div style={{
          borderRadius: 16, padding: "28px 24px", marginBottom: 24, textAlign: "center",
          background: passed ? "hsl(142 76% 36% / 0.08)" : "hsl(0 84% 60% / 0.08)",
          border: `2px solid ${passed ? "hsl(142 76% 36% / 0.3)" : "hsl(0 84% 60% / 0.3)"}`,
        }}>
          <div style={{ fontSize: 52, fontWeight: 800, lineHeight: 1, marginBottom: 8 }}>
            {pct}%
          </div>
          <div style={{ display: "flex", justifyContent: "center", gap: 8, marginBottom: 10 }}>
            {passed
              ? <CheckCircle style={{ width: 22, height: 22, color: "hsl(142, 76%, 36%)" }} />
              : <XCircle style={{ width: 22, height: 22, color: "hsl(0, 84%, 60%)" }} />
            }
            <span style={{ fontSize: 18, fontWeight: 700 }}>
              {passed ? "Passed!" : "Not quite"}
            </span>
          </div>
          <p style={{ fontSize: 14, color: "hsl(var(--muted-foreground))" }}>
            {correctCount} of {result.feedback.length} correct · Need {result.passingScore}% to pass
          </p>
        </div>

        {/* Per-question feedback */}
        <p style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>Question review</p>
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 24 }}>
          {result.feedback.map(item => (
            <FeedbackRow key={item.questionId} item={item} />
          ))}
        </div>

        {/* Actions */}
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          {passed ? (
            <Button
              onClick={onProgressUpdate}
              style={{ background: GREEN_DEEP, color: LIME, flex: 1 }}
            >
              Done
            </Button>
          ) : (
            <Button
              onClick={retake}
              style={{ background: GREEN_DEEP, color: LIME, flex: 1, gap: 6 }}
            >
              <RotateCcw style={{ width: 14, height: 14 }} />
              Try again
            </Button>
          )}
          <Button variant="outline" onClick={retake} style={{ gap: 5 }}>
            <ArrowLeft style={{ width: 13, height: 13 }} />
            Back
          </Button>
        </div>
      </div>
    );
  }

  return null;
}
